import { supabase } from './supabaseClient';
import { DEFAULT_KANBAN_COLUMNS } from '../config/kanbanCategories';
import type { KanbanColumn, KanbanColumnRow } from '../types/database';

/**
 * As colunas do quadro de um projeto (`kanban_columns`).
 *
 * O `id` é textual e estável, e casa com `tasks.status`. Isso é contrato, não
 * detalhe: é o que permite duplicar projeto copiando as colunas com o mesmo id
 * e deixar `tasks.status` atravessar a cópia sem remapeamento nenhum.
 */

/**
 * Nome de exibição de uma linha.
 *
 * `name` nulo quer dizer "coluna canônica": o rótulo vem do dicionário e
 * acompanha o idioma do usuário, como sempre acompanhou. Gravar o texto no
 * seed congelaria o quadro de um usuário em espanhol em português para sempre.
 */
const nomeDeExibicao = (row: Pick<KanbanColumnRow, 'id' | 'name'>): string => {
  if (row.name && row.name.trim()) return row.name;
  return DEFAULT_KANBAN_COLUMNS.find((c) => c.id === row.id)?.name ?? row.id;
};

const rowToColumn = (row: KanbanColumnRow): KanbanColumn => ({
  id: row.id,
  name: nomeDeExibicao(row),
  color: row.color,
  position: row.position,
  wipLimit: row.wip_limit ?? 0,
  category: row.category,
});

export const kanbanColumnService = {
  /**
   * Colunas do projeto, em ordem.
   *
   * Devolve lista VAZIA quando não há nada (projeto anterior ao backfill, ou
   * front que subiu antes de a migration rodar em produção). Quem chama trata
   * vazio caindo nas cinco canônicas: a tela continua idêntica à de hoje, e o
   * front fica seguro de subir antes ou depois do SQL.
   */
  async listByProject(projectId: string): Promise<KanbanColumn[]> {
    const { data, error } = await supabase
      .from('kanban_columns')
      .select('*')
      .eq('project_id', projectId)
      .order('position', { ascending: true });

    if (error) throw error;
    return ((data ?? []) as KanbanColumnRow[]).map(rowToColumn);
  },

  /**
   * Grava o quadro inteiro de uma vez: o editor entrega a lista final.
   *
   * Upsert do que ficou, DELETE do que saiu. A ordem importa: apagar antes de
   * gravar deixaria o quadro sem nenhuma coluna numa janela em que outro
   * usuário poderia carregá-lo.
   *
   * `name` volta a NULO quando o texto é igual ao rótulo canônico daquele id,
   * para a coluna não perder a tradução só porque o usuário abriu o editor,
   * não mudou nada e salvou.
   */
  async saveAll(projectId: string, columns: KanbanColumn[]): Promise<KanbanColumn[]> {
    const payload = columns.map((col, index) => {
      const canonica = DEFAULT_KANBAN_COLUMNS.find((c) => c.id === col.id);
      const nome = col.name.trim();
      return {
        project_id: projectId,
        id: col.id,
        name: canonica && nome === canonica.name ? null : nome,
        color: col.color,
        position: index,
        wip_limit: col.wipLimit ?? 0,
        category: col.category,
      };
    });

    const { data, error } = await supabase
      .from('kanban_columns')
      .upsert(payload, { onConflict: 'project_id,id' })
      .select();

    if (error) throw error;

    // Guarda de segurança: lista vazia geraria `not.id.in.()`, sintaxe inválida
    // no PostgREST. O editor já impede salvar quadro sem coluna (invariantes de
    // entrada e saída), mas este serviço não pode depender disso para não
    // apagar o quadro inteiro se for chamado de outro lugar.
    if (columns.length === 0) {
      return ((data ?? []) as KanbanColumnRow[]).map(rowToColumn);
    }

    const mantidos = columns.map((c) => c.id);
    const { error: erroApagar } = await supabase
      .from('kanban_columns')
      .delete()
      .eq('project_id', projectId)
      .not('id', 'in', `(${mantidos.map((id) => `"${id}"`).join(',')})`);

    if (erroApagar) throw erroApagar;

    return ((data ?? []) as KanbanColumnRow[])
      .map(rowToColumn)
      .sort((a, b) => a.position - b.position);
  },

  /**
   * Move todos os cartões de uma coluna para outra.
   *
   * Existe porque excluir coluna com cartão dentro exige destino: é a regra que
   * o Jira impõe e o motivo é que a alternativa (apagar em silêncio, ou deixar
   * o cartão órfão caindo na primeira coluna) some com trabalho do time sem
   * ninguém perceber. O progresso NÃO é tocado aqui de propósito: mover cartão
   * por consequência de excluir coluna não é o usuário declarando avanço.
   */
  async moverCartoes(projectId: string, deColuna: string, paraColuna: string): Promise<void> {
    const { error } = await supabase
      .from('tasks')
      .update({ status: paraColuna })
      .eq('project_id', projectId)
      .eq('status', deColuna);

    if (error) throw error;
  },

  /**
   * Clona o quadro de um projeto para outro, preservando os ids.
   *
   * Usado por `duplicateProject`. Preservar o id é o que faz `tasks.status`
   * copiado cru continuar apontando para a coluna certa no projeto novo.
   */
  async clonarPara(projectIdOrigem: string, projectIdDestino: string): Promise<void> {
    const colunas = await this.listByProject(projectIdOrigem);
    if (colunas.length === 0) return;
    await this.saveAll(projectIdDestino, colunas);
  },
};

export default kanbanColumnService;
